import api from './axiosInstance';
import { Order, OrderStatus, OrderItem } from './orderApi';
import { User, CreateUserRequest, UpdateUserRoleRequest, RegistrationResponse } from '../types/user';
import { MarkupProfile, UserMarkupResult, CreateMarkupProfileRequest } from '../types/markup';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

interface PaginatedApiResponse<T> {
  success: boolean;
  data?: {
    data: T[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    totalPrice?: number;
  };
  message?: string;
}

// Новые типы для API заказов администратора
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface GetOrdersResult {
  orders: PagedResult<Order>;
  totalPrice: number;
}

export interface OrderFilterParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  status?: OrderStatus;
  fromDate?: string;
  toDate?: string;
  minTotalPrice?: number;
  maxTotalPrice?: number;
  customerEmail?: string;
  customerPhone?: string;
  // Добавляем поля из бэкенда
  userId?: string;
  anonymousId?: string;
  email?: string;
  phone?: string;
  search?: string;
  sortBy?: string;
  sortDesc?: boolean;
}

export interface CreateOrderScratchRequest {
  firstName: string;
  lastName?: string;
  phone: string;
  email?: string;
  deliveryAddress?: string;
  comment?: string;
  notifyUser?: boolean;
  items: CreateOrderItemRequest[];
}

export interface CreateOrderItemRequest {
  name: string;
  code?: string;
  brand?: string;
  count: number;
  pricePerUnit: number;
  deliveryDate: string;
  providerType: number;
}

export const adminApi = {
  // Обновленный метод для получения заказов
  getAllOrders: async (params?: OrderFilterParams): Promise<PaginatedApiResponse<Order>> => {
    try {
      // Фильтруем пустые параметры
      const filteredParams: any = {};
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            filteredParams[key] = value;
          }
        });
      }
      
      const response = await api.get('Order/orders', { params: filteredParams });
      
      // Обрабатываем ответ в соответствии с реальной структурой
      const result = response.data as GetOrdersResult;
      
      return {
        success: true,
        data: {
          data: result.orders?.items || [],
          totalCount: result.orders?.totalCount || 0,
          page: result.orders?.page || 1,
          pageSize: result.orders?.pageSize || 10,
          totalPages: result.orders?.totalPages || 0,
          hasNextPage: result.orders?.hasNextPage || false,
          hasPreviousPage: result.orders?.hasPreviousPage || false,
          totalPrice: result.totalPrice || 0
        }
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при получении заказов'
      };
    }
  },

  // Новый метод для создания заказа с нуля
  createOrderByScratch: async (request: CreateOrderScratchRequest): Promise<ApiResponse<Order>> => {
    try {
      const response = await api.post('Order/scratch', request);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при создании заказа'
      };
    }
  },

  // Метод для получения позиций заказа
  getOrderItems: async (orderId: number): Promise<ApiResponse<OrderItem[]>> => {
    try {
      const response = await api.get(`Order/items/${orderId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при получении позиций заказа'
      };
    }
  },

  updateOrderStatus: async (orderId: number, status: OrderStatus): Promise<ApiResponse<Order>> => {
    try {
      const response = await api.patch(`admin/orders/${orderId}/status`, { status });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при обновлении статуса заказа'
      };
    }
  },

  // Методы для работы с пользователями
  getAllUsers: async (params?: any): Promise<PaginatedApiResponse<UserMarkupResult>> => {
    try {
      // Фильтруем пустые параметры
      const filteredParams: any = {};
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            filteredParams[key] = value;
          }
        });
      }
      
      const response = await api.get('admin/users', { params: filteredParams });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при получении списка пользователей'
      };
    }
  },

  createUser: async (email: string, password: string): Promise<ApiResponse<RegistrationResponse>> => {
    try {
      const response = await api.post('admin/users', null, {
        params: { email, password }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при создании пользователя'
      };
    }
  },

  updateUserRole: async (userId: string, role: string): Promise<ApiResponse<User>> => {
    try {
      const response = await api.post(`admin/users/${userId}/role`, { role });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при обновлении роли пользователя'
      };
    }
  },

  createPhysicalPerson: async (data: any): Promise<ApiResponse<RegistrationResponse>> => {
    try {
      const response = await api.post('admin/users/physical-person', data);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при создании физического лица'
      };
    }
  },

  createLegalEntity: async (data: any): Promise<ApiResponse<RegistrationResponse>> => {
    try {
      const response = await api.post('admin/users/legal-entity', data);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при создании юридического лица'
      };
    }
  },

  // Методы для работы с наценками
  getAllMarkupProfiles: async (): Promise<ApiResponse<MarkupProfile[]>> => {
    try {
      const response = await api.get('markup/profiles');
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при получении списка наценок'
      };
    }
  },

  createMarkupProfile: async (request: CreateMarkupProfileRequest): Promise<ApiResponse<MarkupProfile>> => {
    try {
      const response = await api.post('markup/profiles', request);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при создании наценки'
      };
    }
  },

  assignMarkupProfileToUser: async (userId: string, profileId: number): Promise<ApiResponse<boolean>> => {
    try {
      const response = await api.post(`markup/users/${userId}/profiles/${profileId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при назначении наценки пользователю'
      };
    }
  },

  updateMarkupProfile: async (id: number, request: CreateMarkupProfileRequest): Promise<ApiResponse<MarkupProfile>> => {
    try {
      const response = await api.put(`markup/profiles/${id}`, request);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Ошибка при обновлении наценки'
      };
    }
  },


}; 